import type { VoiceInfo } from '../../types'

import { OFFICIAL_SPEECH_PROVIDER_ID, OFFICIAL_SPEECH_STREAMING_PROVIDER_ID } from './constants'

export { OFFICIAL_CHAT_PROVIDER_ID, OFFICIAL_SPEECH_PROVIDER_ID, OFFICIAL_SPEECH_STREAMING_PROVIDER_ID, OFFICIAL_TRANSCRIPTION_PROVIDER_ID, OFFICIAL_VISION_PROVIDER_ID } from './constants'

const LOCALE_SEPARATOR_RE = /[-_]/
const AUTO_PICK_PROVIDER_IDS = new Set([OFFICIAL_SPEECH_PROVIDER_ID, OFFICIAL_SPEECH_STREAMING_PROVIDER_ID])

function languagePrefix(locale: string) {
  return locale.split(LOCALE_SEPARATOR_RE)[0].toLowerCase()
}

function pickLocaleForUi(uiLocale: string, available: string[]): string {
  if (!available.length)
    return ''
  if (available.includes(uiLocale))
    return uiLocale
  const uiPrefix = languagePrefix(uiLocale)
  const prefixMatch = available.find(code => languagePrefix(code) === uiPrefix)
  if (prefixMatch)
    return prefixMatch
  return available.find(code => code === 'en-US') || available.find(code => code.toLowerCase().startsWith('en')) || available[0]
}

function lookupRecommendedVoiceId(locale: string, map: Record<string, string>): string | undefined {
  if (map[locale])
    return map[locale]

  const prefix = languagePrefix(locale)
  for (const [code, voiceId] of Object.entries(map)) {
    if (languagePrefix(code) === prefix)
      return voiceId
  }
  return undefined
}

function findRecommendedVoice(voices: VoiceInfo[], recommendedMap: Record<string, string>): VoiceInfo | undefined {
  const seen = new Set<string>()
  for (const voiceId of Object.values(recommendedMap)) {
    if (seen.has(voiceId))
      continue
    seen.add(voiceId)
    const voice = voices.find(item => item.id === voiceId)
    if (voice)
      return voice
  }
  return undefined
}

/** Selects a catalog voice for the old official speech ids. Other providers keep the current selection. */
export function pickOfficialSpeechVoice(ctx: {
  activeSpeechProvider: string
  activeSpeechVoiceId: string
  availableVoices: Record<string, VoiceInfo[]>
  uiLocale: string
}) {
  const provider = ctx.activeSpeechProvider
  if (!AUTO_PICK_PROVIDER_IDS.has(provider))
    return

  const providerVoices = ctx.availableVoices[provider]
  if (!providerVoices?.length)
    return
  if (ctx.activeSpeechVoiceId && providerVoices.some(voice => voice.id === ctx.activeSpeechVoiceId))
    return

  const localeCodes = Array.from(new Set(
    providerVoices.flatMap(voice => (voice.languages || []).map(language => language.code).filter(Boolean)),
  )).sort()
  const targetLocale = pickLocaleForUi(ctx.uiLocale, localeCodes)
  const recommendedMap = Object.fromEntries(providerVoices.flatMap(voice =>
    voice.recommendedFor?.map(locale => [locale, voice.id]) ?? [],
  ))
  const recommendedId = lookupRecommendedVoiceId(targetLocale, recommendedMap)
  const speaksLocale = (voice: VoiceInfo, code: string) => (voice.languages || []).some(language => language.code === code)
  const match = (recommendedId && providerVoices.find(voice => voice.id === recommendedId))
    || findRecommendedVoice(providerVoices, recommendedMap)
    || providerVoices.find(voice => speaksLocale(voice, targetLocale))
    || providerVoices.find(voice => speaksLocale(voice, 'en-US'))
    || providerVoices.find(voice => (voice.languages || []).some(language => language.code.toLowerCase().startsWith('en')))
    || providerVoices[0]
  return match?.id
}
